import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import MaFicheAnnuaire from "@/components/MaFicheAnnuaire";
import { toast } from "sonner";
import { Save, Building2 } from "lucide-react";

const PROFESSIONS = [
  ["medecin", "Médecin"], ["psychologue", "Psychologue"], ["infirmier", "Infirmier(ère)"],
  ["dentiste", "Chirurgien-dentiste"], ["sage-femme", "Sage-femme"],
  ["kinesitherapeute", "Kinésithérapeute"], ["autre", "Autre"],
];

export default function MemberProfile() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("/membre/profil").then((d) => {
      setData(d);
      setForm({
        civilite: d.person.civilite || "",
        prenom: d.person.prenom || "",
        nom: d.person.nom || "",
        telephone: d.person.telephone || "",
        statut_pro: d.person.statut_pro || "",
        profession_libre: d.person.profession_libre || "",
        consentement_annuaire: !!d.person.consentement_annuaire,
        consentement_newsletter: !!d.person.consentement_newsletter,
      });
    });
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api("/membre/profil", { method: "PUT", body: form });
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!form || !data) {
    return (
      <AppLayout>
        <div className="h-8 w-8 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
      </AppLayout>
    );
  }

  const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light focus:border-cfhtb-secondary";
  const labelCls = "block text-sm font-medium text-cfhtb-heading mb-1.5";

  return (
    <AppLayout>
      <div data-testid="member-profile" className="max-w-3xl space-y-6 animate-fade-in">
        <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Mon profil</h1>

        {data.institut && (
          <div className="flex items-center gap-3 bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-4">
            <Building2 className="h-5 w-5 text-cfhtb-secondary" />
            <div>
              <p className="text-xs uppercase tracking-wider text-cfhtb-text/80 font-medium">Institut de rattachement</p>
              <p className="text-sm font-medium text-cfhtb-heading">{data.institut.nom}</p>
            </div>
          </div>
        )}

        <form onSubmit={save} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Civilité</label>
              <select data-testid="profile-civilite-select" className={inputCls} value={form.civilite} onChange={(e) => set("civilite", e.target.value)}>
                <option value="">—</option>
                <option value="Mme">Mme</option>
                <option value="M.">M.</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Prénom</label>
              <input data-testid="profile-prenom-input" className={inputCls} value={form.prenom} onChange={(e) => set("prenom", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Nom</label>
              <input data-testid="profile-nom-input" className={inputCls} value={form.nom} onChange={(e) => set("nom", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email (identifiant de connexion)</label>
              <input className={`${inputCls} bg-slate-50`} value={data.person.email_principal} disabled data-testid="profile-email-input" />
            </div>
            <div>
              <label className={labelCls}>Téléphone</label>
              <input data-testid="profile-telephone-input" className={inputCls} value={form.telephone} onChange={(e) => set("telephone", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Profession</label>
              <select data-testid="profile-profession-select" className={inputCls} value={form.statut_pro} onChange={(e) => set("statut_pro", e.target.value)}>
                <option value="">—</option>
                {PROFESSIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Précision (si autre)</label>
              <input data-testid="profile-profession-libre-input" className={inputCls} value={form.profession_libre} onChange={(e) => set("profession_libre", e.target.value)} />
            </div>
          </div>

          <fieldset className="rounded-md border border-slate-200 p-4 space-y-3">
            <legend className="text-sm font-medium text-cfhtb-heading px-1">Consentements</legend>
            <label className="flex items-start gap-3 text-sm text-cfhtb-text cursor-pointer">
              <input
                type="checkbox"
                data-testid="profile-consent-annuaire-checkbox"
                checked={form.consentement_annuaire}
                onChange={(e) => set("consentement_annuaire", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cfhtb-primary focus:ring-cfhtb-light"
              />
              J'accepte que mes coordonnées professionnelles soient publiées dans l'annuaire des praticiens.
            </label>
            <label className="flex items-start gap-3 text-sm text-cfhtb-text cursor-pointer">
              <input
                type="checkbox"
                data-testid="profile-consent-newsletter-checkbox"
                checked={form.consentement_newsletter}
                onChange={(e) => set("consentement_newsletter", e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cfhtb-primary focus:ring-cfhtb-light"
              />
              Je souhaite recevoir la lettre d'information de la CFHTB.
            </label>
          </fieldset>

          <div className="flex justify-end">
            <button
              data-testid="profile-save-button"
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-5 py-2.5 text-sm font-medium hover:bg-[#1f1975] transition-colors disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>

        {data.person.type === "PRATICIEN" && <MaFicheAnnuaire />}

        <p className="text-xs text-cfhtb-text/70">
          Connexion sans mot de passe : chaque connexion se fait via un lien envoyé par email.{" "}
          Aucune donnée bancaire n'est conservée dans cet espace.
        </p>
      </div>
    </AppLayout>
  );
}
